import { TableCellValue, TableData } from 'components/Reports/TableView/TableData.model';
import { logWhenInDevMode, warnWhenInDevMode } from 'utils/debug';
import {
  SOURCE_HEADER_PATTERN,
  NUMERIC_WITH_OPTIONAL_UNIT_PATTERN,
  DEFAULT_CALCULATION_DETAILS,
} from 'helpers/tablePolicy';

export { SOURCE_HEADER_PATTERN, NUMERIC_WITH_OPTIONAL_UNIT_PATTERN };

export const isSourceHeader = (header: string): boolean => SOURCE_HEADER_PATTERN.test(header.trim());

export const extractSourceUrl = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const hrefMatch = value.match(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>/i);
    if (hrefMatch?.[1]) {
      return hrefMatch[1];
    }

    const urlMatch = value.match(/https?:\/\/[^\s"']+/i);
    return urlMatch?.[0];
  }

  if (value && typeof value === 'object' && 'sourceUrl' in (value as any)) {
    const sourceUrl = (value as any).sourceUrl;
    return typeof sourceUrl === 'string' && sourceUrl.trim().length > 0 ? sourceUrl : undefined;
  }

  return undefined;
};

export const isNumericLikePrimitive = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value !== 'string') {
    return false;
  }

  return NUMERIC_WITH_OPTIONAL_UNIT_PATTERN.test(value.trim());
};

export const applyCellTrustPolicy = (cell: TableCellValue): TableCellValue => {
  const hasSourceUrl = typeof cell.sourceUrl === 'string' && cell.sourceUrl.trim().length > 0;
  const shouldDowngradeVerified = cell.verified === true && !hasSourceUrl;
  const shouldAddCalculationDetails = cell.filled === true && !cell.calculationDetails;

  return {
    ...cell,
    verified: shouldDowngradeVerified ? false : cell.verified,
    calculationDetails: shouldAddCalculationDetails ? DEFAULT_CALCULATION_DETAILS : cell.calculationDetails,
  };
};

export const getRowSourceUrl = (row: Record<string, any>, headers: string[]): string | undefined => {
  const sourceHeader = headers.find(isSourceHeader);
  if (!sourceHeader) {
    return undefined;
  }

  const sourceValue = row[sourceHeader];

  if (sourceValue !== null && typeof sourceValue === 'object' && 'value' in sourceValue) {
    return sourceValue.sourceUrl || extractSourceUrl(sourceValue.value);
  }

  return extractSourceUrl(sourceValue);
};

export const sanitizeTableTrustPolicy = (tableData: TableData): TableData => {
  let verifiedDowngradeCount = 0;
  let missingSourceCount = 0;
  const rejectedCells: { col: string; value: unknown }[] = [];
  const injectedCells: { col: string; value: unknown; sourceUrl: string }[] = [];

  const sanitizedRows = tableData.rows.map((row) => {
    const sanitizedRow: Record<string, any> = { ...row };
    const rowSourceUrl = getRowSourceUrl(sanitizedRow, tableData.headers);

    tableData.headers.forEach((header) => {
      const cell = sanitizedRow[header];

      if (cell !== null && typeof cell === 'object' && 'value' in cell) {
        const before = cell as TableCellValue;
        const after = applyCellTrustPolicy(before);
        if (before.verified === true && after.verified === false) {
          verifiedDowngradeCount++;
          rejectedCells.push({ col: header, value: before.value });
        }
        sanitizedRow[header] = after;
        return;
      }

      if (!isSourceHeader(header) && isNumericLikePrimitive(cell)) {
        const hasRowSource = typeof rowSourceUrl === 'string' && rowSourceUrl.trim().length > 0;
        if (hasRowSource) injectedCells.push({ col: header, value: cell, sourceUrl: rowSourceUrl as string });
        else missingSourceCount++;
        sanitizedRow[header] = {
          value: cell,
          filled: false,
          verified: hasRowSource,
          sourceUrl: rowSourceUrl,
        } as TableCellValue;
      }
    });

    return sanitizedRow;
  });

  const logger = (rejectedCells.length > 0 || missingSourceCount > 0) ? warnWhenInDevMode : logWhenInDevMode;
  logger(
    '[Table] "%s" — verified↓ %d, injected %d, missingSource %d%s%s',
    tableData.title ?? '(no title)',
    verifiedDowngradeCount,
    injectedCells.length,
    missingSourceCount,
    injectedCells.length > 0 ? '\n  injected: ' + injectedCells.map(c => `${c.col}=${c.value} → ${c.sourceUrl}`).join(' | ') : '',
    rejectedCells.length > 0 ? '\n  rejected (no sourceUrl): ' + rejectedCells.map(c => `${c.col}=${c.value}`).join(' | ') : ''
  );

  return {
    ...tableData,
    rows: sanitizedRows,
  };
};
